import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Box,
  Grid2,
  SelectChangeEvent,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { API_BASE_URL } from "Utils/apiUtil";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MessageModal } from "./MessageModal";
import Gantt from "rc-gantt";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { extractFileName } from "Utils/file";
import { UserRole, useSessionStorage } from "IF/userSession";
import ReportOutputCsvDialog from "./ReportOutputCsvDialog";
import { createTitle, formatDate } from "Utils/format";
import { DataContext } from "Contexts/DataProvider";
import { ReportAlertChart } from "./ReportAlertChart";
import { ReportTimeAxisChart } from "./ReportTimeAxisChart";
import html2canvas from "html2canvas";
import { useTheme } from "@mui/material";
import { useThemeMode } from "Contexts/ThemeContext";

// ｃｓｖ出力
export type reportForm = {
  startDate: string; // 出力期間開始日
  endDate: string; // 出力期間終了日
  unit: string; // 出力対象単位
  unitText?: string; // 出力対象単位表示
  targetId: string; // 出力対象シリアルNo
  targetName: string; // 出力対象エリア名
  targetText?: string; // 出力対象表示
  schema: string; // 出力形式
};

// レポート出力
export type reportData = {
  tagId: string;
  workerName: string;
  serialNo: string;
  areaName: string;
  startDate: string;
  endDate: string;
  alertTime?: number;
  forcedExitTime?: number;
};

export type workerGroupedData = {
  id: string;
  name: string;
  yDisp: string;
  tasks: {
    areaId: string;
    area: string;
    startDate: string;
    endDate: string;
    alertTime?: number;
    forcedExitTime?: number;
  }[];
};

export type alertGroupedData = {
  id: string;
  name: string;
  yDisp: string;
  tasks: {
    areaId: string;
    area: string;
    alertNum: number;
  }[];
};

// 出力対象
export type optionInfo = {
  id: string;
  name: string;
  text: string;
};

// 凡例
export type explanatoryNote = {
  areaId: string;
  areaName: string;
  areaColor: string;
};

const today = new Date();

const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1); // 明日の日付を設定

export const Report = () => {
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu } = sidebarContext;

  const theme = useTheme();
  const { mode } = useThemeMode();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // 初期値
  const initData: reportForm = {
    startDate: formatDate(today), // 出力期間開始日
    endDate: formatDate(today), // 出力期間終了日
    unit: "", // 出力対象単位
    unitText: "",
    targetId: "", // 出力対象シリアルNo
    targetName: "", // 出力対象エリア名
    targetText: "",
    schema: "", // 出力対象
  };

  // レポートフォーム
  const [reportForm, setReportForm] = useState<reportForm>({
    ...initData,
    unit: "1",
  });

  const [selected, setSelected] = React.useState("アラート回数");

  // レポートデータ
  const [data, setData] = useState<reportData[]>([]);
  const [workerGroupedData, setWorkerGroupedData] = useState<
    workerGroupedData[]
  >([]);
  const [alertGroupedData, setAlertGroupedData] = useState<alertGroupedData[]>(
    []
  );

  // 凡例
  const [legend, setLegend] = useState<explanatoryNote[]>([]);
  const [filteredLegend, setFilteredLegend] = useState<explanatoryNote[]>([]);

  // チャート高さ
  const [chartHeight, setChartHeight] = useState(600);
  const [loading, setLoading] = useState(false);
  // キャプチャ領域
  const captureRef = useRef<HTMLDivElement>(null);

  // messageモーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
  };

  // csv出力ダイアログ
  // csv出力フォーム
  const [outputForm, setOutputForm] = useState<reportForm>(initData);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setOutputForm(initData);
  };
  // 出力対象
  const [targetOptions, setTargetOptions] = useState<optionInfo[]>([]);
  const [targetOptionsCsv, setTargetOptionsCsv] = useState<optionInfo[]>([]);
  const unitMapping: { [key: string]: string } = {
    "0": "タグID",
    "1": "シリアルNo",
    "2": "エリア",
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 2 : 3;
      document.title = createTitle(menuItems[index].text);
      setChoiceMenu(menuItems[index].text);
    }
  }, [userRole, menuItems, setChoiceMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      let windowHeight = window.innerHeight - 270;

      setChartHeight(windowHeight);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // データ取得
    const fetchData = async () => {
      await getData(reportForm, companyCode);
      await getLegendData(reportForm, companyCode);
    };
    fetchData();
  }, [reportForm, companyCode]);

  // レポート情報取得処理
  const getData = async (outputForm: reportForm, companyCode: string) => {
    try {
      setLoading(true); // 読み込み開始
      // API呼び出し
      const response = await axios.get(`${API_BASE_URL}/Report/GetReportInfo`, {
        params: {
          ...outputForm,
          schema: companyCode,
        },
      });
      if (response.data !== "") {
        setData(response.data);
      }
    } catch (error) {
      console.error("レポート情報の取得に失敗しました:", error);
    } finally {
      setLoading(false);
    }
  };

  // 凡例情報取得処理
  const getLegendData = async (outputForm: reportForm, companyCode: string) => {
    try {
      // API呼び出し
      const response = await axios.get(
        `${API_BASE_URL}/Report/GetReportExplanatoryNote`,
        {
          params: { ...outputForm, schema: companyCode },
        }
      );
      if (response.data !== "") {
        setLegend(response.data);
      }
    } catch (error) {
      console.error("レポート情報の取得に失敗しました:", error);
    }
  };

  // 出力対象の選択肢を取得
  const fetchOutputOptions = useCallback(
    async (num: number, form: reportForm) => {
      try {
        if (num === 0) {
          // csv出力Options取得
          const response = await axios.get(
            `${API_BASE_URL}/Report/GetReportTargetOptions`,
            {
              params: { ...form, schema: companyCode },
            }
          );
          if (response.data !== "") {
            setTargetOptionsCsv(response.data);
          }
        } else {
          // レポート出力Options取得
          const response = await axios.get(
            `${API_BASE_URL}/Report/GetReportTargetOptions`,
            {
              params: { ...form, schema: companyCode },
            }
          );
          if (response.data !== "") {
            setTargetOptions(response.data); // setTargetOptions を使用
          }
        }
      } catch (error) {
        console.error("出力対象の取得に失敗しました:", error);
      }
    },
    [companyCode, setTargetOptionsCsv, setTargetOptions]
  );

  // 出力対象単位変更時処理
  useEffect(() => {
    if (outputForm.unit) {
      fetchOutputOptions(0, outputForm);
    }
  }, [outputForm, data, fetchOutputOptions]);

  useEffect(() => {
    if (reportForm.unit) {
      fetchOutputOptions(1, reportForm);
    }
  }, [reportForm, data, fetchOutputOptions]);

  // 出力対象単位変更時処理
  useEffect(() => {
    const workerProcessedData = workerGroupDataById(data);
    setWorkerGroupedData(workerProcessedData);

    const alertProcessedData = alertGroupDataById(data);
    setAlertGroupedData(alertProcessedData);
  }, [data]);

  // レポートフォーム編集時処理
  const handleReportInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setReportForm((prev) => {
      // 更新後の startDate と endDate を仮に決定する
      let newStartDate = prev.startDate;
      let newEndDate = prev.endDate;
      if (name === "startDate") {
        newStartDate = value;
      } else if (name === "endDate") {
        newEndDate = value;
      }
      // 両方の値が設定されている場合、日付の整合性をチェックする
      if (newStartDate && newEndDate) {
        const start = new Date(newStartDate);
        const end = new Date(newEndDate);
        if (start > end) {
          alert("出力期間開始日は出力期間終了日より後の日付にできません。");
          return prev;
        }
      }

      // 整合性が問題なければ更新を反映
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  // csv出力フォーム編集時処理
  const handleCsvInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setOutputForm((prev) => {
      // 更新後の startDate と endDate を仮に決定する
      let newStartDate = prev.startDate;
      let newEndDate = prev.endDate;
      if (name === "startDate") {
        newStartDate = value;
      } else if (name === "endDate") {
        newEndDate = value;
      }
      // 両方の値が設定されている場合、日付の整合性をチェックする
      if (newStartDate && newEndDate) {
        const start = new Date(newStartDate);
        const end = new Date(newEndDate);
        if (start > end) {
          alert("出力期間開始日は出力期間終了日より後の日付にできません。");
          return prev;
        }
      }

      // 整合性が問題なければ更新を反映
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const handleCsvSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const fieldName = event.target.name as string;

    if (fieldName === "unit") {
      // unit の場合、unitMapping を使って表示名を取得
      const displayName = unitMapping[selectedValue] || "";
      setOutputForm((prev) => ({
        ...prev,
        unit: selectedValue,
        unitText: displayName,
        targetId: "",
        targetName: "",
        targetText: "",
      }));
    } else if (fieldName === "target") {
      // target の場合、targetOptionsCsv から対応する option を探す
      const selectedOption = targetOptionsCsv.find(
        (option) => option.id === selectedValue
      );
      setOutputForm((prev) => ({
        ...prev,
        targetId: selectedValue,
        targetName: selectedOption ? selectedOption.name : "",
        targetText: selectedOption ? selectedOption.text : "",
      }));
    }
  };

  const handleReportSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as string;
    const fieldName = event.target.name as string;

    if (fieldName === "unit") {
      // unit の場合、unitMapping を使って表示名を取得
      const displayName = unitMapping[selectedValue] || "";
      setReportForm((prev) => ({
        ...prev,
        unit: selectedValue,
        unitText: displayName,
        targetId: "",
        targetName: "",
        targetText: "",
      }));
    } else if (fieldName === "target") {
      // target の場合、targetOptions から対応する option を探す
      const selectedOption = targetOptions.find(
        (option) => option.id === selectedValue
      );
      setReportForm((prev) => ({
        ...prev,
        targetId: selectedValue,
        targetName: selectedOption ? selectedOption.name : "",
        targetText: selectedOption ? selectedOption.text : "",
      }));
    }
  };

  // 時間軸レポートグループ化ロジック
  const workerGroupDataById = (data: reportData[]): workerGroupedData[] => {
    return data.reduce<workerGroupedData[]>((acc, item) => {
      const existing = acc.find(
        (group) => group.yDisp === ` ${item.workerName}（${item.tagId}）`
      );
      if (existing) {
        existing.tasks.push({
          area: item.areaName,
          areaId: item.serialNo,
          startDate: item.startDate,
          endDate: item.endDate,
          alertTime: item.alertTime,
          forcedExitTime: item.forcedExitTime,
        });
      } else {
        acc.push({
          id: item.tagId,
          name: item.workerName,
          yDisp: ` ${item.workerName}（${item.tagId}）`,
          tasks: [
            {
              area: item.areaName,
              areaId: item.serialNo,
              startDate: item.startDate,
              endDate: item.endDate,
              alertTime: item.alertTime,
              forcedExitTime: item.forcedExitTime,
            },
          ],
        });
      }
      return acc;
    }, []);
  };

  // アラートレポートグループ化ロジック
  const alertGroupDataById = (data: reportData[]): alertGroupedData[] => {
    return data.reduce<alertGroupedData[]>((acc, item) => {
      // 作業者（tagId と workerName の組み合わせ）でグループ化
      let workerGroup = acc.find(
        (group) => group.yDisp === ` ${item.workerName}（${item.tagId}）`
      );
      if (!workerGroup) {
        workerGroup = {
          id: item.tagId,
          name: item.workerName,
          yDisp: ` ${item.workerName}（${item.tagId}）`,
          tasks: [],
        };
        acc.push(workerGroup);
      }

      // ----- idTasks の更新 -----
      // 同じエリアID (serialNo) ごとに alertTime のカウントを行う
      let task = workerGroup.tasks.find(
        (t) => t.areaId === item.serialNo && t.area === item.areaName
      );
      if (!task) {
        task = {
          areaId: item.serialNo,
          area: item.areaName,
          alertNum: 0,
        };
        workerGroup.tasks.push(task);
      }
      // alertTime が null でなければ、カウントを1増やす
      if (item.alertTime !== null) {
        task.alertNum++;
      }
      return acc;
    }, []);
  };

  const handleSelection = (
    event: React.MouseEvent<HTMLElement>,
    newSelection: string | null
  ) => {
    // newSelectionがnullでなければ更新（すでに選択されている状態をクリックした場合はnullとなるため）
    if (newSelection !== null) {
      setSelected(newSelection);
    }
  };

  const formatDateToJapanese = (
    dateString: string | null | undefined
  ): string => {
    if (!dateString) {
      return ""; // または他の適切なデフォルト値
    }
    const date = new Date(dateString.replace(/-/g, "/")); // ハイフンをスラッシュに置換してから Date オブジェクトを作成
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // 月は 0 始まりなので +1 し、2桁になるように 0 で埋める
    const day = date.getDate().toString().padStart(2, "0"); // 日を 2桁になるように 0 で埋める
    return `${year}年${month}月${day}日`;
  };

  // 画面キャプチャ出力処理
  const handleCapture = async () => {
    if (captureRef.current) {
      try {
        const canvas = await html2canvas(captureRef.current);
        const imageDataURL = canvas.toDataURL("image/png");
        // ファイル名作成
        let fileName =
          selected +
          (reportForm.targetText ? `_${reportForm.targetText}` : "") +
          `_${formatDateToJapanese(reportForm.startDate)}～${formatDateToJapanese(reportForm.endDate)}`;

        // 画像をダウンロードする処理
        const a = document.createElement("a");
        a.href = imageDataURL;
        a.download = fileName + ".png"; // ダウンロード時のファイル名
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        setModalOpen(true);
        setMessage("画面キャプチャに失敗しました");
        console.error("画面キャプチャに失敗しました:", error);
      }
    }
  };

  // CSV出力処理
  const handleExportToCsv = async () => {
    try {
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/Report/ExportToCsv`,
        { ...outputForm, schema: companyCode },
        {
          responseType: "blob",
        }
      );
      const contentDisposition = response.headers["content-disposition"];
      // ファイル名取得
      const fileName = extractFileName(contentDisposition);

      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);

      // csv出力
      // BlobとしてCSVデータを取得
      // const blob = new Blob([response.data], { type: "text/csv" });
      
    //const blob = new Blob([bom, response.data], { type: "text/csv;charset=utf-8" });
const blob = new Blob([bom, response.data], {
  type: "application/vnd.ms-excel;charset=utf-8",
});
    console.log("csv blob size", blob.size);
    
      // URLを作成してリンクを作成
      const url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = fileName; // ダウンロード時のファイル名
      downloadLink.click(); // 自動的にクリックしてダウンロード
      window.URL.revokeObjectURL(url); // メモリを解放
    } catch (error) {
      setModalOpen(true);
      setMessage("csv出力に失敗しました");
      console.error("csv出力に失敗しました:", error);
    }
  };

  // Excel出力処理
  const handleExportToExcel = async () => {
    if (selected === "アラート回数") {
      // アラート回数
      exportAlertChart();
    } else {
      // 作業者時間軸
      exportTimeChart();
    }
  };

  //アラート回数Excel出力処理
  const exportAlertChart = async () => {
    try {
      const form = reportForm;
      const data = alertGroupedData;
      const response = await axios.post(
        `${API_BASE_URL}/Report/ExportToExcelAlertChart`,
        { form, data, filteredLegend },
        { responseType: "blob" }
      );

      const contentDisposition = response.headers["content-disposition"];
      // ファイル名取得
      const fileName = extractFileName(contentDisposition);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setModalOpen(true);
      setMessage("Excel出力に失敗しました");
      console.error("エクスポート失敗:", error);
    }
  };

  //作業者時間軸Excel出力処理
  const exportTimeChart = async () => {
    try {
      const form = reportForm;
      const data = workerGroupedData;
      const response = await axios.post(
        `${API_BASE_URL}/Report/ExportToExcelTimeChart`,
        { form, data, filteredLegend },
        { responseType: "blob" }
      );

      const contentDisposition = response.headers["content-disposition"];
      // ファイル名取得
      const fileName = extractFileName(contentDisposition);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setModalOpen(true);
      setMessage("Excel出力に失敗しました");
      console.error("エクスポート失敗:", error);
    }
  };

  const outputButton = () => {
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          onClick={handleCapture}
          variant="contained"
          color="secondary"
          sx={{ width: "15vh" }}
        >
          キャプチャ
        </Button>
        <Button
          onClick={handleOpen}
          sx={{ width: "15vh" }}
          variant="contained"
          color="primary"
        >
          csv出力
        </Button>
        <Button
          onClick={handleExportToExcel}
          sx={{ width: "15vh" }}
          variant="contained"
          color="info"
        >
          グラフ出力
        </Button>
      </Box>
    );
  };

  return (
    <>
      <MessageModal
        open={modalOpen}
        message={message}
        handleClose={closeModal}
      />
      {/* csv出力ダイアログ */}
      <ReportOutputCsvDialog
        open={open}
        handleClose={handleClose}
        handleInputChange={handleCsvInputChange}
        handleSelectChange={handleCsvSelectChange}
        handleOutput={handleExportToCsv}
        outputForm={outputForm}
        targetOptions={targetOptionsCsv}
      />
      <Box
        ref={captureRef}
        sx={{
          gap: 1,
          maxWidth: "bodyWidth",
          backgroundColor: theme.palette.custom.page,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            pb: {
              xs: 1,
              sm: 2,
              md: 2,
              lg: 2,
            },
            pt: 0,
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "column",
              lg: "row",
            },
            alignItems: {
              xs: "flex-start",
              sm: "flex-start",
              md: "flex-start",
              lg: "center",
            },
          }}
        >
          <Box
            sx={{
              display: {
                xs: "flex",
                sm: "flex",
                md: "flex",
                lg: "none",
              },
              gap: 1,
              pb: 1.5,
            }}
          >
            {outputButton()}
          </Box>
          <ToggleButtonGroup
            value={selected}
            exclusive
            onChange={handleSelection}
            aria-label="選択ボタン"
          >
            <ToggleButton
              value="アラート回数"
              aria-label="アラート回数"
              sx={{
                width: { xs: "20vh", sm: "30vh" },
                "&.Mui-selected": {
                  backgroundColor: mode === "dark" ? "#DC143C" : "#ff9999",
                },
              }}
            >
              アラート回数
            </ToggleButton>
            {/* <ToggleButton
              value="作業者時間軸"
              aria-label="作業者時間軸"
              sx={{
                width: { xs: "20vh", sm: "30vh" },
                "&.Mui-selected": {
                  backgroundColor: "rgba(102, 153, 255, 0.8)",
                },
              }}
            >
              作業者時間軸
            </ToggleButton> */}
          </ToggleButtonGroup>
          {/* 出力ボタン */}
          <Box
            sx={{
              display: {
                xs: "none",
                sm: "none",
                md: "none",
                lg: "flex", // lg以上で表示
              },
              gap: 1, // display: flex と併用
            }}
          >
            {outputButton()}
          </Box>
        </Box>
        <Grid2 container spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
          {/* 出力期間 */}
          <Grid2 size={{ xs: 3, sm: 3 }}>
            <TextField
              size="small"
              label="出力期間開始日"
              type="date"
              name="startDate"
              value={reportForm.startDate}
              onChange={handleReportInputChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                input: {
                  ...(mode === "dark" && {
                    "&::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                    },
                  }),
                },
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 3, sm: 3 }}>
            <TextField
              size="small"
              label="出力期間終了日"
              type="date"
              name="endDate"
              value={reportForm.endDate}
              onChange={handleReportInputChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                input: {
                  ...(mode === "dark" && {
                    "&::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                    },
                  }),
                },
              }}
            />
          </Grid2>
          {isXs && <Grid2 size={{ xs: 6 }}></Grid2>}
          {/* 出力対象単位 */}
          <Grid2 size={{ xs: 3, sm: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel shrink>出力対象単位</InputLabel>
              <Select
                size="small"
                value={reportForm.unit}
                name="unit"
                onChange={handleReportSelectChange}
                label="出力対象単位"
                displayEmpty
              >
                <MenuItem value="1">シリアルNo</MenuItem>
                <MenuItem value="2">エリア</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
          {/* 出力対象 */}
          <Grid2 size={{ xs: 3, sm: 3 }}>
            <FormControl fullWidth disabled={!reportForm.unit}>
              <InputLabel shrink>出力対象</InputLabel>
              <Select
                size="small"
                value={
                  targetOptions.some(
                    (option) => option.id === reportForm.targetId
                  )
                    ? reportForm.targetId
                    : ""
                }
                name="target"
                onChange={handleReportSelectChange}
                label="出力対象"
                displayEmpty
              >
                <MenuItem value="" style={{ minHeight: 36 }}></MenuItem>
                {targetOptions.map((option, index) => (
                  <MenuItem key={`${option.id}-${index}`} value={option.id}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 4, sm: 3 }}></Grid2>
        </Grid2>
        {/* ガントチャート */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mt: 2,
            }}
          >
            <CircularProgress />
            <Typography sx={{ textAlign: "center", fontSize: "0.875rem" }}>
              データ取得中です。
            </Typography>
          </Box>
        ) : selected === "アラート回数" ? (
          alertGroupedData.length > 0 ? (
            <ReportAlertChart
              alertGroupedData={alertGroupedData}
              reportForm={reportForm}
              legend={legend}
              chartHeight={chartHeight}
              filteredLegend={filteredLegend}
              setFilteredLegend={setFilteredLegend}
            />
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                marginTop: 2,
                fontSize: "0.875rem",
                minWidth: "700px",
              }}
            >
              指定の条件ではデータが存在しません。条件を変更してください。
            </Typography>
          )
        ) : workerGroupedData.length > 0 ? (
          <ReportTimeAxisChart
            startDate={reportForm.startDate}
            endDate={reportForm.endDate}
            workerGroupedData={workerGroupedData}
            reportForm={reportForm}
            legend={legend}
            chartHeight={chartHeight}
            filteredLegend={filteredLegend}
            setFilteredLegend={setFilteredLegend}
          />
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              marginTop: 2,
              fontSize: "0.875rem",
              minWidth: "700px",
            }}
          >
            指定の条件ではデータが存在しません。条件を変更してください。
          </Typography>
        )}
      </Box>
    </>
  );
};
export default Report;
